import React, { useState } from 'react';
import PropTypes from 'prop-types';
import IconButton from '@mui/material/IconButton';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import DialogTitle from '@mui/material/DialogTitle';
import generateConditionLabel from '../Condition/generateConditionLabel';

function getLabelForTraceBackStep(step) {
  switch (step.type) {
    case 'module':
      return `Module: ${step.moduleName}`;
    case 'answer_to_question':
      return `${step.questionText} -> ${step.answerTitle}`;
    case 'conditional':
      return `IF ${generateConditionLabel(step.conditionalView.condition)}:`;
    case 'dxhx_pt_factor':
      return `DxHx Pt Factors: ${step.dxhxName}`;
    case 'sx_of_dxhx':
      return `Symptom of DxHx: DxHx="${step.dxhxName}" -> Symptom="${step.symptomName}"`;
    case 'rfv':
      return `RFV: ${step.rfvName}`;
    case 'request_for_medication':
      return `Request for medication: ${step.medicationName}`;
    case 'medication_usage':
      return `Medication usage: ${step.medicationName}`;
    case 'error':
      return `Error: ${step.error}`;
    case 'unused':
      return 'The following step is unused and can be ignored. Should be removed in next DB cleanup.';
    default:
      return `Unknown step type: ${step.type}`;
  }
}

function renderActionTraceBack(contentTraceItems) {
  const lastStep = contentTraceItems.at(-1);
  if (!lastStep) return null;
  const firstStepLabel = getLabelForTraceBackStep(lastStep);
  return (
    <ul key={firstStepLabel}>
      <li>
        {firstStepLabel}
        {renderActionTraceBack(contentTraceItems.slice(0, -1)) || ''}
      </li>
    </ul>
  );
}

function renderModuleTraceBack(intakePlans, contentTraces) {
  return (
    <div>
      <h3>Intake Plans</h3>
      <ul>
        {intakePlans?.map((intakePlan) => (
          <li key={intakePlan.id}>
            {intakePlan.referenceCode}
            {' '}
            {intakePlan.description}
          </li>
        ))}
      </ul>
      <div>
        <h3>Actions</h3>
        {contentTraces?.map((contentTrace) => renderActionTraceBack(contentTrace.steps))}
      </div>
    </div>
  );
}

export default function WhereButton({ whereService, title = 'Where' }) {
  const [intakePlans, setIntakePlans] = useState(null);
  const [contentTraces, setContentTraces] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = () => {
    setIsOpen(false);
    setIntakePlans(null);
    setContentTraces(null);
  };

  const handleOpen = async () => {
    try {
      const whereResponse = await whereService();
      if (!whereResponse.data) {
        setErrorMessage(`An error occurred. Please inform the tech team. WhereResponse was ${whereResponse}`);
        setIsOpen(true);
        return;
      }
      const {
        intakePlans: fetchedIntakePlans,
        contentTrace: fetchedContentTraces,
      } = whereResponse.data;
      setIntakePlans(fetchedIntakePlans);
      setContentTraces(fetchedContentTraces);
    } catch (error) {
      setErrorMessage(`An error occurred. A log of the error has been generated for tech to review. Please let them know. Error: ${error}`);
    } finally {
      setIsOpen(true);
    }
  };

  const contents = errorMessage ? (
    <DialogContent sx={{ mt: 0, pt: 0 }}>
      {errorMessage}
    </DialogContent>
  ) : (
    <>
      <DialogTitle sx={{ mb: 0, pb: 0 }}>
        {title}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
        size="large"
      >
        <CloseIcon fontSize="large" />
      </IconButton>
      <DialogContent sx={{ mt: 0, pt: 0 }}>
        {renderModuleTraceBack(intakePlans, contentTraces)}
      </DialogContent>
    </>
  );
  return (
    <>
      <IconButton
        disabled={!whereService}
        onClick={handleOpen}
      >
        <QuestionMarkIcon />
      </IconButton>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
      >
        {contents}
      </Dialog>
    </>
  );
}

WhereButton.propTypes = {
  whereService: PropTypes.func,
  title: PropTypes.string,
};

WhereButton.defaultProps = {
  whereService: null,
  title: 'Where',
};
